/**
 * Object Storage 업로드 & 문서 미리보기 유틸리티
 * v1 원본: wehago-conference/src/utils/ObjectStorage.js
 *
 * PDF/Office 파일 → Object Storage 업로드 → 페이지별 이미지 URL 변환
 */
import axios from 'axios';
import CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';
import apiConfig from '@/api/config';
import apiManager from '@/api';
import { getAuthInfo } from '@/api/signHeader';

const baseGuestUrl = apiConfig.baseGuestUrl;

// ─── 서명 유틸 ─────────────────────────────────────────────────────────────

async function getSignature(endpoint: string): Promise<string> {
  const apiUrl = `${baseGuestUrl}/get_token/?url=${endpoint}`;
  const { data } = await axios.get<{ cur_date: string; token: string }>(apiUrl);
  const token = `${data.cur_date}${data.token}`;
  return CryptoJS.enc.Base64.stringify(CryptoJS.SHA256(`${endpoint}${token}`));
}

// ─── 내부 API 호출 ─────────────────────────────────────────────────────────

interface UploadSignatureResult {
  resultList: Array<{ url: string; Auth: string; Date: string }>;
}

async function uploadSignature(
  file: File,
  signature: string,
  newFileName: string,
  serviceKey: { service_key: string; service_code: string },
): Promise<UploadSignatureResult> {
  const url = `${baseGuestUrl}/ObjectStorageCommon/services/login`;
  const headers = {
    'Content-Type': 'application/json',
    service: 'objectStorageService',
    method: 'getUploadSignatureNoToken',
    signature,
  };

  const data = {
    BucketType: 'S',
    ServiceCode: serviceKey.service_code,
    ServiceKey: serviceKey.service_key,
    FileName: newFileName,
    'Content-Type': file.type,
    isPublicURL: true,
  };

  const result = await axios({ method: 'POST', url, headers, data });
  return result.data;
}

async function uploadFileMetadata(
  file: File,
  newFileUUID: string,
  serviceKey: { service_key: string; service_code: string },
): Promise<{ resultCode: string; resultList: unknown[] }> {
  const endpoint = '/ObjectStorageCommon/services/login';
  const url = `${baseGuestUrl}${endpoint}`;
  const signature = await getSignature(endpoint);

  const headers = {
    'Content-Type': 'application/json',
    service: 'objectStorageService',
    method: 'setFileUploadSuccessNoToken',
    signature,
  };

  const param = {
    BucketType: 'S',
    ServiceCode: serviceKey.service_code,
    ServiceKey: serviceKey.service_key,
    FileName: newFileUUID,
    'Content-Type': file.type,
    isPublicURL: true,
    Ext: getFileExt(file.name),
    realFileName: file.name,
    isMakeThumbNailURL: true,
    isWedrive: false,
    WedriveTokenID: '',
    TargetFileUniqueKey: '',
  };

  const result = await axios({ method: 'POST', url, headers, data: param });
  return result.data;
}

async function getOfficePreView(params: {
  FileName: string;
  serviceKey: { service_key: string; service_code: string };
  BucketType: string;
  cno: string | undefined;
  Ext: string;
  contentType: string;
  method?: string; // Add optional method parameter
  forceNoToken?: boolean;
}): Promise<{ resultList?: Array<{ resources: string[] }> }> {
  const endpoint = '/ObjectStorageCommon/services/public';
  const url = `${baseGuestUrl}${endpoint}`;

  const authInfo = getAuthInfo(url);
  const { obfuscateValue1, obfuscateValue2, obfuscateValue3, clientId, token, info } = authInfo;

  // 비회원(게스트)이거나 forceNoToken이 true인 경우:
  // AUTH_A_TOKEN이 없거나 만료되어 회원용 API가 실패할 때, 오브젝트스토리지 비회원 전용 NoToken API로 우회한다.
  if (!token || params.forceNoToken) {
    const noTokenEndpoint = '/ObjectStorageCommon/services/login';
    const noTokenUrl = `${baseGuestUrl}${noTokenEndpoint}`;
    const noTokenSignature = await getSignature(noTokenEndpoint);
    const isImage = (params.method || 'getOfficePreView') === 'getImageURL';
    const noTokenMethod = isImage ? 'getImageURLNoToken' : 'getOfficePreviewNoToken';

    const noTokenHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      service: 'objectStorageService',
      method: noTokenMethod,
      signature: noTokenSignature,
    };

    const noTokenData = isImage
      ? {
          BucketType: params.BucketType,
          ServiceCode: params.serviceKey.service_code,
          ServiceKey: params.serviceKey.service_key,
          FileName: params.FileName,
          ExpireTime: '60', // Add ExpireTime as per API spec
          cno: params.cno,
        }
      : {
          BucketType: params.BucketType, // API 문서에는 없으나 서버(makeParam) 내부에서 필수로 참조하여 NPE(9999)가 발생할 수 있으므로 추가
          BucketName: '',
          FileName: params.FileName,
          Ext: params.Ext,
          isFullPreview: true,
          ServiceCode: params.serviceKey.service_code,
          ServiceKey: params.serviceKey.service_key,
          cno: params.cno,
          companyNo: params.cno,
        };

    const noTokenResult = await axios({ method: 'POST', url: noTokenUrl, headers: noTokenHeaders, data: noTokenData });
    return noTokenResult.data;
  }

  const signature = await getSignature(endpoint);

  const headers: Record<string, string | boolean> = {
    'Content-Type': 'application/x-www-form-urlencoded',
    service: 'objectStorageService',
    method: params.method || 'getOfficePreView',
    signature,
    [info.transactionId.key]: obfuscateValue1,
    [info.ws.key]: obfuscateValue2,
    [info.ts.key]: String(obfuscateValue3),
    'client-id': clientId,
    authorization: token,
  };

  const data = new URLSearchParams({
    cno: params.cno ?? '',
    BucketType: params.BucketType,
    ServiceCode: params.serviceKey.service_code,
    ServiceKey: params.serviceKey.service_key,
    FileName: params.FileName,
    'Content-Type': params.contentType,
    Ext: params.Ext,
    isWedrive: 'false',
    isFullPreview: 'true',
  });

  try {
    const result = await axios({ method: 'POST', url, headers: headers as Record<string, string>, data });

    // 만약 회원 API가 실패(E002, 또는 토큰 파싱 에러로 인한 9999 등)를 반환하면 비회원 API로 재시도
    if (result.data?.errors || result.data?.resultCode === '9999' || result.data?.resultCode === 'ERROR') {
      console.warn('[ObjectStorage] Member API returned errors or 9999, falling back to NoToken API...', result.data);
      return getOfficePreView({ ...params, forceNoToken: true });
    }

    return result.data;
  } catch (err: any) {
    // 401 Unauthorized 등 HTTP 레벨의 에러 발생 시 NoToken으로 재시도
    if (!params.forceNoToken) {
      console.warn('[ObjectStorage] Member API threw an error, falling back to NoToken API...', err);
      return getOfficePreView({ ...params, forceNoToken: true });
    }
    throw err;
  }
}

// ─── 파일 확장자 유틸 ──────────────────────────────────────────────────────

function getFileExt(filename: string): string {
  if (!filename) return '';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot + 1);
}

// ─── Object Storage 파일 조회 ──────────────────────────────────────────────

/**
 * Object Storage에서 파일 URL을 조회 (비인증)
 * v1 원본: wehago-conference/src/utils/JitsiVirtualBackground/View.js
 */
export async function viewObjectStorageFile(
  filename: string,
  serviceKey: { service_key: string; service_code: string },
): Promise<{ resultCode: string; resultList: string[] } | null> {
  try {
    const endpoint = '/ObjectStorageCommon/services/login';
    const url = `${baseGuestUrl}${endpoint}`;
    const signature = await getSignature(endpoint);

    const headers = {
      'Content-Type': 'application/json',
      service: 'objectStorageService',
      method: 'getImageURLNoToken',
      signature,
    };

    const data = {
      BucketType: 'S',
      ServiceCode: serviceKey.service_code,
      ServiceKey: serviceKey.service_key,
      FileName: filename,
    };

    const result = await axios({ method: 'POST', url, headers, data });
    if (result.data.resultCode === '0000') return result.data;
    return null;
  } catch (err) {
    console.error('[ObjectStorage] 파일 조회 실패:', err);
    return null;
  }
}

// ─── 공개 API ──────────────────────────────────────────────────────────────

export interface UploadFilesResult {
  newFileName: string;
  orgFileName: string;
  thumbnail: string;
  fileSize: number;
  bucketType: string;
}

/**
 * Object Storage 파일 다운로드
 */
export async function downloadObjectStorageFile(params: {
  FileName: string;
  BucketType: string;
  realFileName: string;
  cno?: string;
  forceNoToken?: boolean;
}): Promise<void> {
  const token = localStorage.getItem('AUTH_A_TOKEN');
  let authResult;

  try {
    const serviceKeyRes = await apiManager.getVideoServiceKey();
    const serviceKey = {
      service_key: serviceKeyRes.service_key,
      service_code: serviceKeyRes.service_code,
    };

    if (!token || params.forceNoToken) {
      // 비회원 시그니처 발급
      const endpoint = '/ObjectStorageCommon/services/login';
      const signature = await getSignature(endpoint);
      const noTokenUrl = `${baseGuestUrl}${endpoint}`;
      
      const result = await axios({
        method: 'POST',
        url: noTokenUrl,
        headers: {
          'Content-Type': 'application/json',
          service: 'objectStorageService',
          method: 'getDownloadSignatureNoToken',
          signature,
        },
        data: {
          BucketType: params.BucketType,
          ServiceCode: serviceKey.service_code,
          ServiceKey: serviceKey.service_key,
          FileName: params.FileName,
          cno: params.cno,
        },
      });
      
      if (result.data.resultCode !== '0000') throw new Error(result.data.serverMsg);
      authResult = result.data.resultList[0];
    } else {
      // 회원 시그니처 발급
      const endpoint = '/ObjectStorageCommon/services/common';
      const memberUrl = `${apiConfig.baseApiUrl}${endpoint}`;
      const signature = await getSignature(endpoint);
      const authInfo = getAuthInfo(memberUrl);
      const { obfuscateValue1, obfuscateValue2, obfuscateValue3, clientId, info } = authInfo;

      // text/plain;charset-UTF-8 으로 전송 (쿼리스트링 형태)
      const dataStr = `cno=${params.cno || ''}&ServiceKey=${serviceKey.service_key}&ServiceCode=${serviceKey.service_code}&BucketType=${params.BucketType}&FileName=${params.FileName}`;

      const headers = {
        'Content-Type': 'text/plain;charset-UTF-8',
        service: 'objectStorageService',
        method: 'getDownloadSignature',
        signature,
        [info.transactionId.key]: obfuscateValue1,
        [info.ws.key]: obfuscateValue2,
        [info.ts.key]: String(obfuscateValue3),
        'client-id': clientId,
        Authorization: `Bearer ${token}`,
      };

      const result = await axios({
        method: 'POST',
        url: memberUrl,
        headers: headers as Record<string, string>,
        data: dataStr,
      });

      if (result.data?.errors || result.data?.resultCode === '9999' || result.data?.resultCode === 'ERROR') {
         console.warn('[ObjectStorage] Download Member API failed, falling back to NoToken');
         return downloadObjectStorageFile({ ...params, forceNoToken: true });
      }
      if (result.data.resultCode !== '0000') throw new Error(result.data.serverMsg);
      authResult = result.data.resultList[0];
    }

    // S3에서 실제 파일 다운로드 (GET)
    const fileResponse = await axios({
      method: 'GET',
      url: authResult.url,
      headers: {
        Authorization: authResult.Auth,
        'x-amz-date': authResult.Date,
      },
      responseType: 'blob',
    });

    const blob = new Blob([fileResponse.data], { type: fileResponse.headers['content-type'] as string });
    
    if (navigator.userAgent.match('CriOS')) {
      const reader = new FileReader();
      reader.onload = function() {
        if (reader.result) window.location.href = reader.result as string;
      };
      reader.readAsDataURL(blob);
    } else {
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = params.realFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    }
  } catch (err: any) {
    if (!params.forceNoToken) {
       console.warn('[ObjectStorage] Member Download threw error, falling back to NoToken...', err);
       return downloadObjectStorageFile({ ...params, forceNoToken: true });
    }
    console.error('[ObjectStorage] File download failed:', err);
    throw err;
  }
}

/**
 * 파일을 WEHAGO Object Storage에 업로드
 * 1) 서비스 키 조회
 * 2) 업로드 서명 → S3 pre-signed URL 획득
 * 3) S3에 파일 PUT
 * 4) 파일 메타데이터 등록
 */
export async function uploadFiles(file: File): Promise<UploadFilesResult | null> {
  try {
    const newFileUUID = uuidv4();
    const result = await apiManager.getVideoServiceKey();
    const serviceKey = {
      service_key: result.service_key,
      service_code: result.service_code,
    };

    const endpoint = '/ObjectStorageCommon/services/login';
    const signature = await getSignature(endpoint);
    const unAuthResult = await uploadSignature(file, signature, newFileUUID, serviceKey);
    const unAuthData = unAuthResult.resultList[0];

    await axios({
      method: 'PUT',
      url: unAuthData.url,
      headers: {
        Authorization: unAuthData.Auth,
        'x-amz-date': unAuthData.Date,
        'Content-Type': file.type,
      },
      data: file,
    });

    const { resultCode, resultList } = await uploadFileMetadata(file, newFileUUID, serviceKey);
    if (resultCode === '0000') {
      const thumbnailPath = resultList[0] !== undefined ? String(resultList[0]) : '';
      const fullThumbnailUrl = thumbnailPath && !thumbnailPath.startsWith('http')
        ? `${apiConfig.thumbnailBaseUrl}${thumbnailPath}`
        : thumbnailPath;

      return {
        newFileName: newFileUUID,
        orgFileName: file.name,
        thumbnail: fullThumbnailUrl,
        fileSize: resultList[2] !== undefined ? Number(resultList[2]) : 0,
        bucketType: 'S',
      };
    }
    return null;
  } catch (err) {
    console.error('[ObjectStorage] 파일 업로드 실패:', err);
    return null;
  }
}

/**
 * Office/PDF 파일을 페이지별 이미지 URL로 변환
 * Object Storage의 getOfficePreView API 사용
 */
export async function previewFilesUnauthorized(params: {
  FileName: string;
  BucketType: string;
  cno: string | undefined;
  Ext: string;
  contentType: string;
  forceNoToken?: boolean;
}): Promise<{ resultList?: Array<{ resources: string[] }> } | null> {
  try {
    const result = await apiManager.getVideoServiceKey();
    const serviceKey = {
      service_key: result.service_key,
      service_code: result.service_code,
    };

    return await getOfficePreView({
      FileName: params.FileName,
      serviceKey,
      BucketType: params.BucketType,
      cno: params.cno,
      Ext: params.Ext,
      contentType: params.contentType,
      forceNoToken: params.forceNoToken,
      method: params.contentType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(params.Ext.toLowerCase()) ? 'getImageURL' : 'getOfficePreView',
    });
  } catch (err) {
    console.error('[ObjectStorage] 문서 미리보기 실패:', err);
    return null;
  }
}
